import { useEffect, useState } from 'react';
import { Car, ParkingSquare } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Button } from '@/components/Shared/Button';
import { useMyVehicleTripsStore, type UnparkedTrip } from '@/store/useMyVehicleTripsStore';
import { formatDateTime, parseUtcTimestamp } from '@/utils/dateUtils';
import { ParkVehicleDialog } from './ParkVehicleDialog';

interface UnparkedVehiclesNoticeProps {
  /** Loads the list itself (dashboard, time clock page); the trip dialog has it loaded already. */
  fetchOnMount?: boolean;
}

const DATE_TIME: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * Vehicles the employee left without a reading (FAFD-68) — an open item like a missing
 * clock-out (FACF-112): until they are parked, the employee cannot start a trip as driver. Each row
 * leads straight to "Fahrzeug abstellen". Shows nothing while everything is parked.
 */
export function UnparkedVehiclesNotice({ fetchOnMount = true }: UnparkedVehiclesNoticeProps) {
  const { t } = useTranslation('vehicles');
  const unparked = useMyVehicleTripsStore((s) => s.unparked);
  const fetchMyUnparked = useMyVehicleTripsStore((s) => s.fetchMyUnparked);
  const [parking, setParking] = useState<UnparkedTrip | null>(null);

  useEffect(() => {
    if (fetchOnMount) void fetchMyUnparked();
  }, [fetchOnMount, fetchMyUnparked]);

  if (unparked.length === 0) return null;

  return (
    <div
      className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:bg-amber-900/10 dark:border-amber-800"
      role="status"
    >
      <div className="flex items-start gap-2">
        <Car className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" strokeWidth={2} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-amber-900 dark:text-amber-200">{t('notParked.title')}</p>
          <p className="text-[12px] text-amber-800 dark:text-amber-300 mt-0.5">{t('notParked.hint')}</p>
        </div>
      </div>
      <ul className="mt-3 space-y-2">
        {unparked.map((trip) => (
          <li
            key={trip.entryId}
            className="flex flex-wrap items-center gap-2 rounded-lg bg-white/70 dark:bg-slate-900/40 px-3 py-2"
          >
            <span className="flex-1 min-w-0 text-[13px] text-slate-900 dark:text-slate-100 tabular-nums">
              <span className="font-mono">{trip.licensePlate}</span>
              {' · '}
              {t('notParked.endedAt', { time: formatDateTime(parseUtcTimestamp(trip.endedAt), DATE_TIME) })}
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setParking(trip)}
              leftIcon={<ParkingSquare className="w-3.5 h-3.5" strokeWidth={2} />}
            >
              {t('notParked.action')}
            </Button>
          </li>
        ))}
      </ul>
      <ParkVehicleDialog trip={parking} onClose={() => setParking(null)} />
    </div>
  );
}
